import { html, View } from 'rune-ts';
import klass from './SectionTitle.module.scss';
import { Title, type TitleProps } from '../../atoms/Title/Title';
import {
  ArrowPlugin,
  ButtonCheckboxPlugin,
  CalendarPlugin,
  DropdownPlugin,
  SeeMoreLinkPlugin,
} from './plugins';
import { compact, filter, isNil, map, pipe, toArray } from '@fxts/core';
import { htmlIf } from '@services/MarppleShop/shared/util';
import { font } from '@services/MarppleShop/shared/typography';

type Plugin =
  | typeof CalendarPlugin
  | typeof ButtonCheckboxPlugin
  | typeof DropdownPlugin
  | typeof ArrowPlugin
  | typeof SeeMoreLinkPlugin;

type PluginInstance = InstanceType<Plugin>;

type PluginInstanceView = InstanceType<PluginInstance['ViewClass']>;

/**
 * SectionTitle 에서 사용하는 데이터 타입
 * - title_props: 제목 관련 속성
 * - plugins: 사용할 플러그인 인스턴스 또는 클래스 리스트
 * - ssr: mui 에서 사용할 경우 각 플러그인 뷰 인스턴스들을 toHtmlSSR 해주는 옵션
 */
export interface SectionTitleData {
  title_props: TitleProps & {
    description?: string;
  };
  plugins?: (PluginInstance | Plugin | undefined)[];
  ssr?: true;
}

/**
 * SectionTitle 컴포넌트
 * - 타이틀과 함께 다양한 플러그인 뷰들을 렌더링 하는 컴포넌트
 */
export class SectionTitle extends View<SectionTitleData> {
  static readonly sizeOptions = {
    xLarge: {
      size_class: klass.xLarge,
      description_font: font('14_medium'),
    },
    large: {
      size_class: klass.large,
      description_font: font('12_medium'),
    },
    medium: {
      size_class: klass.medium,
    },
    small: {
      size_class: klass.small,
    },
    xSmall: {
      size_class: klass.xSmall,
    },
  } as const;

  private readonly _titleView: Title;

  protected readonly plugin_views: PluginInstanceView[];

  constructor(data: SectionTitleData) {
    super(data);

    data.title_props.size = this.size;

    this._titleView = new Title(data.title_props);

    this.plugin_views = this._initializePlugins(data);
  }

  override template() {
    const { size_class } = SectionTitle.sizeOptions[this.size];

    return html`<div class="${klass.section_title} ${size_class}">
      <div class="${klass.title_wrapper}">
        ${this._titleView}
        <div class="${klass.plugin_views}">${this.renderPluginViews()}</div>
      </div>
      ${htmlIf(this._makeDescriptionHtml(this.data.title_props.size), !!this.data.title_props.description)}
    </div>`;
  }

  /**
   * 플러그인 초기화
   * - 클래스인 경우 인스턴스화하고 ViewClass 추출
   * - 이미 인스턴스인 경우 그대로 사용
   */
  private _initializePlugins(data: SectionTitleData): PluginInstanceView[] {
    return pipe(
      data.plugins || [],
      filter((Plugin) => !isNil(Plugin)),
      map((Plugin) => {
        if (this.isPluginConstructor(Plugin)) {
          return new Plugin();
        }
        return Plugin;
      }),
      map((Plugin) => Plugin.makeViewInstance()),
      compact,
      toArray,
    );
  }

  private _makeDescriptionHtml(size: TitleProps['size']) {
    const { description } = this.data.title_props;

    if (size === 'xLarge' || size === 'large') {
      const size_option = SectionTitle.sizeOptions[size];

      return html` <p class="${klass.description} ${size_option.description_font}">${description}</p> `;
    }

    return html``;
  }

  private renderPluginViews() {
    return this.data.ssr
      ? this.plugin_views.map((plugin_view) => plugin_view.toHtmlSSR())
      : this.plugin_views;
  }

  // js 환경 대응
  private isPluginConstructor(obj: unknown): obj is Plugin {
    return typeof obj === 'function';
  }

  get pluginViews() {
    return this.plugin_views;
  }

  get size() {
    return this.data.title_props.size || 'xLarge';
  }

  get theme() {
    return this.data.title_props.theme || 'light';
  }
}
